import { ApiMethods, ApiTags, appAPI } from '.';

export interface BuilderMessage {
  id: string;
  builderSessionId: string;
  role: 'user' | 'assistant' | 'tool' | string;
  content: string;
  toolName?: string;
  toolInput?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BuilderSession {
  id: string;
  appBuilderId: string;
  claudeSessionId?: string;
  summary?: string;
  mode?: 'code' | 'plan' | string;
  // Non-empty while a turn is in flight; cleared when the turn completes.
  // Drives the chat thinking indicator and composer lock.
  activeTurnId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListBuilderMessagesResponse {
  data: BuilderMessage[];
  nextToken?: string;
}

export const getBuilderSessionEntityTag = (id: string) => ({
  type: ApiTags.builderSessions as const,
  id,
});

export const getBuilderSessionMessagesTag = (builderSessionId: string) => ({
  type: ApiTags.builderMessages as const,
  id: `MESSAGES-${builderSessionId}`,
});

/**
 * Merge an incoming WS message event into an existing transcript list.
 *
 * The same message ID can arrive twice (once from the GET hydrate, once from
 * a near-simultaneous WS event), and MODIFY events arrive throughout streaming
 * as content is appended. Dedupe by ID and merge fields so the latest payload
 * wins on overlap.
 *
 * Pure function — exported so reducers/tests can call it without RTK plumbing.
 */
export const mergeBuilderMessages = (
  existing: BuilderMessage[],
  incoming: BuilderMessage[],
): BuilderMessage[] => {
  if (incoming.length === 0) {
    return existing;
  }

  const merged = [...existing];
  const indexById = new Map<string, number>();
  merged.forEach((message, index) => {
    indexById.set(message.id, index);
  });

  for (const message of incoming) {
    if (!message.id) continue;
    const existingIndex = indexById.get(message.id);
    if (existingIndex === undefined) {
      indexById.set(message.id, merged.length);
      merged.push(message);
    } else {
      merged[existingIndex] = { ...merged[existingIndex], ...message };
    }
  }

  return merged;
};

export const builderSessionApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getBuilderSession: build.query<
      BuilderSession,
      { appBuilderId: string; sessionId: string }
    >({
      query: ({ appBuilderId, sessionId }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${sessionId}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getBuilderSessionEntityTag(arg.sessionId)],
    }),
    listBuilderSessionMessages: build.query<
      ListBuilderMessagesResponse,
      {
        appBuilderId: string;
        builderSessionId: string;
        limit?: number;
        nextToken?: string;
      }
    >({
      query: ({ appBuilderId, builderSessionId, limit, nextToken }) => {
        const qs = new URLSearchParams();
        if (limit != null) qs.set('limit', String(limit));
        if (nextToken) qs.set('nextToken', nextToken);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return {
          path: `/app-builders/${appBuilderId}/sessions/${builderSessionId}/messages${suffix}`,
          method: ApiMethods.get,
          options: {},
        };
      },
      providesTags: (_, __, arg) => [
        getBuilderSessionMessagesTag(arg.builderSessionId),
      ],
    }),
  }),
});

export const { useGetBuilderSessionQuery, useListBuilderSessionMessagesQuery } =
  builderSessionApi;
